import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import {
  PhoneNumber,
  WaitingQueueStatus,
  Country,
  City,
  Zipcode,
  NumberLookupType,
  BookNumberPayload,
  AnsweringMachineInfo,
  AmBookPayload,
  PreFilledRegulationData,
  RegulationAddressDetails,
  RegulationPrefill,
  RegulationFormValue,
  VoxboneStatusResult,
  NumberTag,
  mapRegulationAddress,
  getRegulationPrefill,
  AddressCheckRequest,
  AddressCheckResponse,
} from './cc-numbers.model';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class CcNumbersService {
  private readonly http = inject(HttpClient);

  private basePath(psId: number): string {
    return `${environment.phoneServiceUrl}/phoneservices/${psId}`;
  }

  // --- Numbers CRUD ---

  getNumbers(psId: number): Observable<PhoneNumber[]> {
    return this.http.get<PhoneNumber[]>(`${this.basePath(psId)}/numbers`);
  }

  /**
   * Book a new callcenter/hidden number. Multipart form-data: `phoneNumberData` (JSON blob,
   * incl. `regulationInformation` for regulated cities) plus optional `proofOfAddressFile` /
   * `proofOfIdFile`. Mirrors the regulation-fix multipart contract (see fixRegulationAddress).
   * Returns the created number.
   */
  bookNumber(psId: number, payload: BookNumberPayload): Observable<PhoneNumber> {
    const fd = new FormData();
    fd.append('phoneNumberData',
      new Blob([JSON.stringify(payload.phoneNumberData)], { type: 'application/json' }));
    if (payload.addressProofFile) fd.append('proofOfAddressFile', payload.addressProofFile);
    if (payload.identityProofFile) fd.append('proofOfIdFile', payload.identityProofFile);
    return this.http.post<PhoneNumber>(`${this.basePath(psId)}/numbers`, fd);
  }

  editNumberType(psId: number, numberId: number, type: string): Observable<PhoneNumber> {
    return this.http.patch<PhoneNumber>(
      `${this.basePath(psId)}/numbers/${numberId}`,
      { type },
    );
  }

  deleteNumber(psId: number, numberId: number, employeeId: string): Observable<void> {
    return this.http.delete<void>(`${this.basePath(psId)}/numbers/${numberId}`, {
      params: { employeeId },
    });
  }

  /** Add a tag to a number. The PS-API normalizes (trim + UPPERCASE) and returns the stored tag; 400 on empty/invalid. */
  addTag(psId: number, number: string, name: string): Observable<NumberTag> {
    return this.http.post<NumberTag>(
      `${this.basePath(psId)}/numbers/${encodeURIComponent(number)}/tags/normalized`,
      { name },
    );
  }

  /**
   * Remove a tag from a number by its tag id. The PS-API returns the number as a plain-text body
   * (not JSON) on success, so we must read it as text — otherwise HttpClient throws a JSON parse
   * error on an otherwise-successful 200.
   */
  deleteTag(psId: number, number: string, tagId: number): Observable<unknown> {
    return this.http.delete(
      `${this.basePath(psId)}/numbers/${encodeURIComponent(number)}/tags/${tagId}`,
      { responseType: 'text' },
    );
  }

  /**
   * Stored regulation address for a number (PII/KYC). PS-API constraint: the endpoint only resolves
   * for REJECTED / NO_REGULATION_ADDRESS_FOUND / FAILED states and returns 400 otherwise — callers
   * must gate on the regulation status. Returns null when no address data is present in the response.
   */
  getRegulationAddress(psId: number, number: string): Observable<RegulationAddressDetails | null> {
    return this.http
      .get<PreFilledRegulationData>(`${this.basePath(psId)}/numbers/${encodeURIComponent(number)}/regulationAddresses`)
      .pipe(map(res => mapRegulationAddress(res)));
  }

  /** Prefill for the fix form: field requirements + current (rejected) values from the same endpoint. */
  getRegulationPrefill(psId: number, number: string): Observable<RegulationPrefill> {
    return this.http
      .get<PreFilledRegulationData>(`${this.basePath(psId)}/numbers/${encodeURIComponent(number)}/regulationAddresses`)
      .pipe(map(res => getRegulationPrefill(res)));
  }

  /**
   * Re-submit a number's regulation (the repair PUT). Multipart: `regulationInformation` (JSON),
   * `numberType` (JSON), and optional `proofOfAddressFile` / `proofOfIdFile`. Returns the resulting VoxboneStatus.
   * Mirrors the booking endpoint's multipart contract; only resolvable for failed/rejected numbers.
   */
  fixRegulationAddress(psId: number, number: string, value: RegulationFormValue): Observable<VoxboneStatusResult> {
    const fd = new FormData();
    fd.append('regulationInformation',
      new Blob([JSON.stringify(value.regulationInformation)], { type: 'application/json' }));
    // PS-API binds numberType via @RequestPart into an enum: needs a JSON part, a plain text part yields 415
    fd.append('numberType', new Blob([JSON.stringify(value.numberType)], { type: 'application/json' }));
    if (value.addressProofFile) fd.append('proofOfAddressFile', value.addressProofFile);
    if (value.identityProofFile) fd.append('proofOfIdFile', value.identityProofFile);
    // The PS-API returns a bare VoxboneStatus enum — Jackson serializes it as a JSON string
    // ("VERIFICATION_REQUESTED"), not an object. Normalize both shapes to { status }.
    return this.http
      .put(`${this.basePath(psId)}/numbers/${encodeURIComponent(number)}/regulationAddresses`, fd)
      .pipe(map((res: unknown): VoxboneStatusResult => ({
        status: typeof res === 'string' ? res : ((res as VoxboneStatusResult)?.status ?? String(res)),
      })));
  }

  // --- Country / City / Zip cascade ---

  /**
   * `lookupType` = the bookable inventory to list — `FAX` for fax booking, as old CMT
   * number.dialog `setCountries`/`setCities` pass `faxMode ? 'FAX' : 'CALLCENTER'`.
   * @ticket EB-3809
   */
  getCountries(brandId: number, lookupType: NumberLookupType = 'CALLCENTER'): Observable<Country[]> {
    return this.http.get<Country[]>(`${environment.phoneServiceUrl}/countries`, {
      params: { brandId: brandId.toString(), phoneNumberType: lookupType },
    });
  }

  getCities(isoCountryCode: string, brandId: number, lookupType: NumberLookupType = 'CALLCENTER'): Observable<City[]> {
    return this.http.get<City[]>(`${environment.phoneServiceUrl}/countries/${isoCountryCode}/cities`, {
      params: { brandId: brandId.toString(), phoneNumberType: lookupType },
    });
  }

  getZipcodes(isoCountryCode: string, cityPrefix: string): Observable<Zipcode[]> {
    return this.http.get<Zipcode[]>(
      `${environment.phoneServiceUrl}/countries/${isoCountryCode}/cities/${cityPrefix}/zipcodes`,
    );
  }

  /** Geocode address check run before booking a regulated number (PS-6489).
   *  Non-DEU countries answer NOT_APPLICABLE, so the caller does not have to pre-filter. */
  checkAddress(
    isoCountryCode: string,
    cityPrefix: string,
    payload: AddressCheckRequest,
  ): Observable<AddressCheckResponse> {
    return this.http.post<AddressCheckResponse>(
      `${environment.phoneServiceUrl}/countries/${isoCountryCode}/cities/${cityPrefix}/addresschecks`,
      payload,
    );
  }

  // --- Answering Machine ---

  /** Load full AM info including linked numbers, password, language.
   *  API returns 200 with empty body when no AM exists — treat as null. */
  getAmInfo(psId: number): Observable<AnsweringMachineInfo | null> {
    return this.http.get(`${this.basePath(psId)}/answeringmachine`, { responseType: 'text' }).pipe(
      map(text => {
        if (!text || text.trim() === '') return null;
        return JSON.parse(text) as AnsweringMachineInfo;
      }),
    );
  }

  /** Book AM for a specific number. Body includes email, language, type, withDummyNumber */
  bookAnsweringMachine(psId: number, number: string, employeeId: string, payload: AmBookPayload): Observable<any> {
    return this.http.post(
      `${this.basePath(psId)}/numbers/${number}/answeringmachine`,
      payload,
      { params: { employeeId } },
    );
  }

  /** Deactivate AM for a specific number (voicebox stays) */
  deactivateAnsweringMachine(psId: number, number: string): Observable<any> {
    return this.http.put(
      `${this.basePath(psId)}/numbers/${number}/answeringmachine`,
      null,
    );
  }

  /** Update AM language for the entire PS */
  updateAnsweringMachineLanguage(psId: number, language: string): Observable<any> {
    return this.http.put(
      `${this.basePath(psId)}/answeringmachine`,
      null,
      { params: { language } },
    );
  }

  /** Cancel AM for entire PS (removes VOICEBOX number) */
  cancelAnsweringMachine(psId: number, employeeId: string): Observable<any> {
    return this.http.delete(`${this.basePath(psId)}/answeringmachine`, {
      params: { employeeId },
    });
  }

  // --- Waiting Queue ---

  /** WQ API returns plain string like "ACTIVE", not JSON object */
  getWaitingQueueStatus(psId: number): Observable<WaitingQueueStatus> {
    return this.http.get(`${this.basePath(psId)}/waitingqueue`, { responseType: 'text' }).pipe(
      map(text => {
        const status = text.replace(/"/g, '').trim();
        return { status } as WaitingQueueStatus;
      }),
    );
  }

  activateWaitingQueue(psId: number): Observable<any> {
    return this.http.post(`${this.basePath(psId)}/waitingqueue`, null);
  }

  deactivateWaitingQueue(psId: number): Observable<any> {
    return this.http.delete(`${this.basePath(psId)}/waitingqueue`);
  }
}
